import { ForwardedRef, forwardRef, useImperativeHandle, useRef } from 'react';

import { Flex } from '@revenuemarket/cds';
import { colors } from '@revenuemarket/design';
import { overlays } from '@revenuemarket/overlays';
import { Typography } from '@revenuemarket/ui';
import { ICellRendererComp, ICellRendererParams } from 'ag-grid-community';

import { LabeledBankAccountTransaction } from '~apis/cash-flow-overview/transactions/getTransactions';
import { LabelSelect, LabelValue } from '~components/CashStatus/LabelSelect';
import TransactionMappingModal from '~components/Mapping/TransactionMappingModal';
import { flattenTree } from '~components/ui/HorizontalTreeTable/Table';
import { useCategoriesTree } from '~hooks/queries/categories/query';
import { useDemoAwareCompanyId } from '~hooks/useDemoAwareCompanyId';
import { css } from '~libs/emotion';
import { getSplittedText } from '~utils/transaction/getSplittedText';

import { LabeledCardBilling } from '../editors/AGGridTransactionLabelSelectEditor';

interface Props
  extends ICellRendererParams<
    LabeledBankAccountTransaction | LabeledCardBilling,
    LabelValue | undefined
  > {
  transactionType?: 'in' | 'out' | 'all';
  skipAnalysis?: boolean;
  skipCardPayment: boolean;
  agGridRowData: (LabeledBankAccountTransaction | LabeledCardBilling)[];
}

const AGGridTransactionLabelSelectRenderer = (
  { data, value, api, skipAnalysis, agGridRowData }: Props,
  ref: ForwardedRef<ICellRendererComp>,
) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { companyId } = useDemoAwareCompanyId();
  const { data: categoriesData } = useCategoriesTree({ companyId }, false);
  const treeCategories = categoriesData?.Data?.data?.tree?.children;
  const categories = flattenTree(treeCategories ?? []);

  // FIXME: transaction 업데이트 안되는 이슈
  const labelCount =
    agGridRowData?.find((row) => row.transactionId === data?.transactionId)?.labelCount ?? 0;
  const isSplitted = labelCount > 1;

  useImperativeHandle(
    ref,
    () => ({
      getGui: () => {
        return containerRef.current ?? document.body;
      },
      refresh: () => {
        return true;
      },
    }),
    [],
  );

  if (data == null) {
    return null;
  }

  if (value == null && !Boolean(skipAnalysis)) {
    return (
      <Flex ref={containerRef} align="center" style={{ height: '100%', paddingLeft: '8px' }}>
        <Typography variant="caption1" color={colors.grayscale400}>
          분석중..
        </Typography>
      </Flex>
    );
  }

  if (isSplitted && data.transactionId) {
    const targetCategory = categories.find((c) => c.value?.categoryLabelKey === data?.labelKey);

    return (
      <Flex
        align="center"
        style={{ height: '100%', paddingLeft: '8px' }}
        onClick={(e) => {
          e.stopPropagation();
          // TODO: 분할 거래내역 시에만 click 이벤트
          overlays.open(({ isOpen, onClose }) => (
            <TransactionMappingModal
              open={isOpen}
              onCancel={onClose}
              transactionId={data.transactionId}
              showTaxInvoice={false}
            />
          ));
        }}
      >
        <Typography variant="caption1" color={colors.grayscale400} useEllipsis>
          {getSplittedText({
            labelCount,
            allCount: labelCount,
            category: targetCategory?.value?.name,
          })}
        </Typography>
      </Flex>
    );
  }

  return (
    <Flex ref={containerRef} align="center" style={{ height: '100%' }}>
      <LabelSelect
        clearCellEvent={() => {
          api.clearFocusedCell();
        }}
        transaction={data}
        categories={categories}
        value={value}
        open={false}
        disable
        className={css`
          width: 100%;
        `}
      />
    </Flex>
  );
};

export default forwardRef(AGGridTransactionLabelSelectRenderer);
